import {
  ApplicationConfig,
  EnvironmentProviders,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  Provider,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  RouteReuseStrategy,
  TitleStrategy,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import {TranslateModule} from '@ngx-translate/core';
import {
  DatePipe,
  DecimalPipe,
  registerLocaleData,
} from '@angular/common';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {MAT_SELECT_CONFIG} from '@angular/material/select';
import {Constants} from './shared/common/constants/constants';
import {MAT_ICON_DEFAULT_OPTIONS} from '@angular/material/icon';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeIt from '@angular/common/locales/it';
import localeItExtra from '@angular/common/locales/extra/it';
import * as Sentry from '@sentry/angular';
import {TraceService} from '@sentry/angular';
import {provideEnvironmentNgxMask} from 'ngx-mask';
import {provideHighlightOptions} from 'ngx-highlightjs';
import {CookieService} from 'ngx-cookie-service';
import {provideMatomo} from 'ngx-matomo-client';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import {provideStore} from '@ngrx/store';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {NameQuestionCategoryPipe} from './shared/pipes/name-question-category/name-question-category.pipe';
import {NameQuestionPipe} from './shared/pipes/name-question/name-question.pipe';
import {TranslatableStringPipe} from './shared/pipes/translatable-string/translatable-string.pipe';
import {TranslatableStringDataPipe} from './shared/pipes/translatable-string-data/translatable-string-data.pipe';
import {GccVersionTitlePipe} from './shared/pipes/gcc-version-title/gcc-version-title.pipe';
import {NogaTitlePipe} from './shared/pipes/noga-title/noga-title.pipe';
import {ClientNamePipe} from './modules/broker/pipes/client-name/client-name.pipe';
import {JwtInterceptor} from './shared/services/infrastructure/auth/interceptors/jwt.interceptor';
import {ErrorInterceptor} from './shared/services/infrastructure/auth/interceptors/error.interceptor';
import {
  APP_DATE_FORMATS,
  AppDateAdapter,
} from './shared/services/infrastructure/utility/date/datepicker/format-datepicker';
import {store} from './shared/store';
import {injectColorSchema} from './shared/services/infrastructure/page/browser/color-schema/inject-color-schema';
import {environment} from '../environments/environment';
import {APP_ROUTES} from './app.routes';
import {provideApollo} from 'apollo-angular';
import {HttpLink} from 'apollo-angular/http';
import {InMemoryCache} from '@apollo/client/core';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import {CachingRouteReuseStrategy} from './route-caching-reuse-strategy';
import {CustomPreloadingStrategy} from './custom-preloading.strategy';
import {SessionStorageService} from './shared/services/infrastructure/page/storage/session-storage/session-storage.service';
import {
  MATOMO_CONFIG,
  MatomoService,
} from './shared/services/infrastructure/page/matomo/matomo.service';
import {isMatomoConfigured} from './shared/common/functions/matomo';
import {SngLoadingChunkErrorHandler} from './shared/services/infrastructure/page/error/error-handler/sng-loading-chunk-error-handler.service';
import {OVERLAY_DEFAULT_CONFIG} from '@angular/cdk/overlay';
import {AppTitleStrategy} from './app-title.strategy';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de', localeDeExtra);
registerLocaleData(localeFr, 'fr', localeFrExtra);
registerLocaleData(localeIt, 'it', localeItExtra);

export function createSelectConfigFactory(): Provider | EnvironmentProviders {
  return {
    provide: MAT_SELECT_CONFIG,
    useFactory: () => {
      return {
        overlayPanelClass: ['overlay-pane--sobrado-select', injectColorSchema().selectOverlayClass],
      };
    },
  };
}

export function overlayConfigFactory(): Provider | EnvironmentProviders {
  return {
    provide: OVERLAY_DEFAULT_CONFIG,
    useValue: {usePopover: false},
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      APP_ROUTES,
      withPreloading(CustomPreloadingStrategy),
      // need to disable scroll to top if add query params
      withInMemoryScrolling({scrollPositionRestoration: 'disabled'}),
    ),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideStore(store),
    createSelectConfigFactory(),
    overlayConfigFactory(),
    provideEnvironmentNgxMask(),
    provideHighlightOptions({
      coreLibraryLoader: () => import('highlight.js/lib/core'),
      lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
      languages: {
        json: () => import('highlight.js/lib/languages/json'),
        javascript: () => import('highlight.js/lib/languages/javascript'),
      },
    }),
    provideStoreDevtools({maxAge: 25, logOnly: environment.production}),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      return {
        cache: new InMemoryCache(),
        link: httpLink.create({
          uri: environment.apiGraphQl,
          extractFiles: body => extractFiles(body, isExtractableFile),
        }),
      };
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: provideTranslateHttpLoader({
          prefix: './assets/i18n/',
          suffix: `.json?version=${environment.hashTranslate}`,
        }),
      }),
    ),
    // custom pipes
    NameQuestionCategoryPipe,
    NameQuestionPipe,
    TranslatableStringPipe,
    TranslatableStringDataPipe,
    GccVersionTitlePipe,
    NogaTitlePipe,
    ClientNamePipe,
    // system services/pipes
    CookieService,
    DecimalPipe,
    DatePipe,
    {provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
    {provide: DateAdapter, useClass: AppDateAdapter},
    {provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS},
    {provide: MAT_ICON_DEFAULT_OPTIONS, useValue: {fontSet: Constants.MATERIAL_SYMBOLS_FONT_SET}},
    {provide: RouteReuseStrategy, useClass: CachingRouteReuseStrategy},
    {provide: TitleStrategy, useClass: AppTitleStrategy},
    // Sentry config
    {provide: Sentry.TraceService, deps: [Router]},
    {provide: ErrorHandler, useClass: SngLoadingChunkErrorHandler},
    provideAppInitializer(() => { inject(TraceService); }),
    provideAppInitializer(() => { inject(SessionStorageService); }),
    provideMatomo(() => ({
      mode: 'deferred',
      disabled: !isMatomoConfigured(inject(MATOMO_CONFIG)),
      enableLinkTracking: false,
      trackAppInitialLoad: false,
    })),
    provideAppInitializer(() => inject(MatomoService).startPageViewTracking()),
  ],
};
